// src/components/DeleteConfirm/index.jsx
// Delete / restore confirmations for soft-deletable records (customers, walk-in
// and pledge cases).
//
//   const { confirmDelete, confirmPermanentDelete, confirmRestore, contextHolder, isAdmin } = useDeleteConfirm();
//   confirmDelete({ what: 'case WIC-0012', onDelete: (permanent) => api.delete(id, { permanent }) });
//
// Everyone gets a normal (soft) delete — the record is hidden and an admin can
// restore it. Admins additionally get "Delete permanently", which always asks a
// second time and says plainly that it is an admin-only hard delete.
import React from 'react';
import { Modal, Button, Space, Typography } from 'antd';
import { ExclamationCircleFilled, DeleteOutlined, UndoOutlined } from '@ant-design/icons';
import { usePermissions } from '../../hooks/usePermissions';

const { Text, Paragraph } = Typography;

export function useDeleteConfirm() {
  const [modal, contextHolder] = Modal.useModal();
  const { role } = usePermissions();
  const isAdmin = (role || '').toLowerCase() === 'admin';

  const confirmPermanentDelete = ({ what, onDelete }) => modal.confirm({
    title: `Permanently delete ${what}?`,
    icon: <ExclamationCircleFilled style={{ color: '#cf1322' }} />,
    content: (
      <>
        <Paragraph style={{ marginBottom: 8 }}>
          <Text strong type="danger">HARD DELETE (admin only).</Text>
        </Paragraph>
        <Paragraph style={{ marginBottom: 0 }}>
          This removes {what} from the database for good, together with any linked records the
          system deletes with it. It <Text strong>cannot be restored</Text>.
        </Paragraph>
      </>
    ),
    okText: 'Yes, delete permanently',
    okButtonProps: { danger: true },
    cancelText: 'Cancel',
    onOk: () => onDelete(true),
  });

  const confirmDelete = ({ what, onDelete }) => {
    if (!isAdmin) {
      return modal.confirm({
        title: `Delete ${what}?`,
        icon: <ExclamationCircleFilled style={{ color: '#faad14' }} />,
        content: 'It will be removed from all lists. An admin can restore it if it was deleted by mistake.',
        okText: 'Delete',
        okButtonProps: { danger: true },
        onOk: () => onDelete(false),
      });
    }

    let instance;
    const close = () => instance?.destroy();
    instance = modal.confirm({
      title: `Delete ${what}?`,
      icon: <ExclamationCircleFilled style={{ color: '#faad14' }} />,
      width: 480,
      content: (
        <>
          <Paragraph style={{ marginBottom: 8 }}>
            <Text strong>Delete</Text> hides it from all lists. It stays in the database and can be restored.
          </Paragraph>
          <Paragraph style={{ marginBottom: 0 }}>
            <Text strong type="danger">Delete permanently</Text> removes it from the database for good (admin only).
          </Paragraph>
        </>
      ),
      footer: () => (
        <Space style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }} wrap>
          <Button onClick={close}>Cancel</Button>
          <Button
            icon={<DeleteOutlined />}
            onClick={async () => { close(); await onDelete(false); }}
          >
            Delete
          </Button>
          <Button
            danger
            type="primary"
            onClick={() => { close(); confirmPermanentDelete({ what, onDelete }); }}
          >
            Delete permanently…
          </Button>
        </Space>
      ),
    });
    return instance;
  };

  const confirmRestore = ({ what, onRestore }) => modal.confirm({
    title: `Restore ${what}?`,
    icon: <UndoOutlined style={{ color: '#1677ff' }} />,
    content: 'It will show up in the normal lists again.',
    okText: 'Restore',
    onOk: onRestore,
  });

  return { confirmDelete, confirmPermanentDelete, confirmRestore, contextHolder, isAdmin };
}
